import { mergeClasses } from "@fluentui/react-components";
import { istObjekt, neueListenId, textOderNichts, useListenStyles } from "./listenStil";
import { asArray } from "./structuredAnswers";
import { ziffernFilter } from "./GrossZahlEingabe";

/**
 * Kunst am Bau / im Freien — mehrere Objekte, je mit Vorgaben statt Freitext
 * (Fachabstimmung 31.08.2026: *„mehrere Objekte erfassbar, mit Vorgaben
 * (Skulpturen, Kunstobjekte …)"*).
 *
 * Die Liste liegt unter **einem** Answer-Key `kunstObjekte`. Bis 10/2026 gab
 * es nur `kunstBeschreibung` und `kunstWert`; `kunstAusAntworten` macht daraus
 * beim Öffnen den ersten Eintrag.
 */

export const KUNST_ARTEN = [
  "Skulptur / Plastik",
  "Relief",
  "Wandbild / Mosaik",
  "Glaskunst / Bleiglasfenster",
  "Brunnen / Wasserspiel",
  "Lichtinstallation",
  "Sonstiges",
] as const;

export const KUNST_MATERIALIEN = [
  "Bronze / Metall",
  "Edelstahl",
  "Naturstein",
  "Beton",
  "Keramik",
  "Glas",
  "Holz",
  "Sonstiges",
] as const;

export const KUNST_STANDORTE = [
  "am Gebäude (außen)",
  "im Gebäude",
  "im Freien auf dem Grundstück",
] as const;

export const KUNST_WERTBASIS = ["Gutachten", "Rechnung", "Schätzung"] as const;

export type KunstObjekt = {
  id: string;
  art?: string;
  material?: string;
  standort?: string;
  beschreibung?: string;
  /** Wert in Euro. */
  wert?: string;
  wertBasis?: string;
};

export const KUNST_ALT_SCHLUESSEL = ["kunstBeschreibung", "kunstWert"] as const;

const FELDER = ["art", "material", "standort", "beschreibung", "wert", "wertBasis"] as const;

export const asKunstListe = (value: unknown): KunstObjekt[] =>
  asArray<unknown>(value)
    .filter(istObjekt)
    .map((e, idx) => {
      const objekt: KunstObjekt = { id: textOderNichts(e.id) ?? `kunst-${idx}` };
      for (const f of FELDER) {
        const v = textOderNichts(e[f]);
        if (v) objekt[f] = v;
      }
      return objekt;
    });

export const kunstAusAntworten = (a: Record<string, unknown>): KunstObjekt[] => {
  const liste = asKunstListe(a.kunstObjekte);
  if (liste.length > 0) return liste;
  const beschreibung = textOderNichts(a.kunstBeschreibung);
  const wert = textOderNichts(a.kunstWert);
  if (!beschreibung && !wert) return [];
  const alt: KunstObjekt = { id: "kunst-alt" };
  if (beschreibung) alt.beschreibung = beschreibung;
  if (wert) alt.wert = wert;
  return [alt];
};

type Props = {
  entries: KunstObjekt[];
  onChange: (next: KunstObjekt[]) => void;
};

const Auswahl = ({
  label,
  name,
  value,
  optionen,
  onChange,
  className,
  labelClass,
  feldClass,
}: {
  label: string;
  name: string;
  value?: string;
  optionen: readonly string[];
  onChange: (v: string) => void;
  className: string;
  labelClass: string;
  feldClass: string;
}) => (
  <div className={feldClass}>
    <span className={labelClass}>{label}</span>
    <select
      className={className}
      name={name}
      aria-label={label}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">Bitte wählen</option>
      {optionen.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
      {value && !optionen.includes(value) && <option value={value}>{value}</option>}
    </select>
  </div>
);

const KunstListe = ({ entries, onChange }: Props) => {
  const styles = useListenStyles();
  const update = (id: string, patch: Partial<KunstObjekt>) =>
    onChange(entries.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  const remove = (id: string) => onChange(entries.filter((e) => e.id !== id));
  const add = () => onChange([...entries, { id: neueListenId("kunst") }]);

  return (
    <div className={styles.root}>
      {entries.length === 0 && <span className={styles.empty}>Noch kein Kunstobjekt erfasst.</span>}
      {entries.map((entry, idx) => {
        const auswahl = (feld: "art" | "material" | "standort" | "wertBasis", label: string, optionen: readonly string[]) => (
          <Auswahl
            label={`Kunstobjekt ${idx + 1} — ${label}`}
            name={`kunstObjekte-${idx}-${feld}`}
            value={entry[feld]}
            optionen={optionen}
            onChange={(v) => update(entry.id, { [feld]: v })}
            className={styles.input}
            labelClass={styles.label}
            feldClass={styles.field}
          />
        );
        return (
          <div key={entry.id} className={styles.card}>
            <div className={styles.head}>
              <span className={styles.badge}>{idx + 1}</span>
              <span className={styles.title}>{entry.art || `Kunstobjekt ${idx + 1}`}</span>
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => remove(entry.id)}
                aria-label="Kunstobjekt entfernen"
              >
                ×
              </button>
            </div>
            <div className={styles.grid}>
              {auswahl("art", "Art", KUNST_ARTEN)}
              {auswahl("material", "Material", KUNST_MATERIALIEN)}
              {auswahl("standort", "Standort", KUNST_STANDORTE)}
              <div className={mergeClasses(styles.field, styles.fullSpan)}>
                <span className={styles.label}>Beschreibung</span>
                <input
                  className={styles.input}
                  name={`kunstObjekte-${idx}-beschreibung`}
                  aria-label={`Kunstobjekt ${idx + 1} — Beschreibung`}
                  placeholder="z. B. Künstler, Titel, Maße"
                  value={entry.beschreibung ?? ""}
                  onChange={(e) => update(entry.id, { beschreibung: e.target.value })}
                />
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Wert (€)</span>
                <input
                  className={styles.input}
                  name={`kunstObjekte-${idx}-wert`}
                  aria-label={`Kunstobjekt ${idx + 1} — Wert (€)`}
                  inputMode="decimal"
                  value={entry.wert ?? ""}
                  onChange={(e) =>
                    update(entry.id, { wert: ziffernFilter(e.target.value, { dezimal: true }) })
                  }
                />
              </div>
              {auswahl("wertBasis", "Wert laut", KUNST_WERTBASIS)}
            </div>
          </div>
        );
      })}
      <button type="button" className={styles.addBtn} onClick={add}>
        + Kunstobjekt hinzufügen
      </button>
    </div>
  );
};

export default KunstListe;
